import React, { useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import { formatClock } from '../utils/timeZone';
import {
  buildSegments,
  buildAxisLabels,
  getCameraMarkers,
  toPercent,
} from '../utils/timeline';
import { LEGEND_MESSAGES } from '../utils/messageKeys';
import { StatusMessage } from './StatusMessage';
import { TimelineHandle } from './TimelineHandle';

// The limit labels are aligned to their end of the track so the panel edge does not cut them.
const LABEL_ALIGN_CLASS = { start: 'edge-start', tick: '', end: 'edge-end' };

const TrailNotice = ({ trail, hasSegments }) => {
  if (trail.isLoading) return <StatusMessage id="requestVideo_trail_loading" />;
  if (trail.isError) return <StatusMessage id="requestVideo_trail_error" tone="error" onRetry={trail.refetch} />;
  if (!hasSegments) return <StatusMessage id="requestVideo_trail_empty" />;
  return null;
};

const Legend = () => {
  const { formatMessage } = useIntl();
  return (
    <ul className="request-video-legend">
      {Object.entries(LEGEND_MESSAGES).map(([kind, id]) => (
        <li key={kind} className={`request-video-legend-${kind}`}>
          {formatMessage({ id })}
        </li>
      ))}
      <li className="request-video-legend-camera">{formatMessage({ id: 'requestVideo_legend_camera' })}</li>
    </ul>
  );
};

export const TrailTimeline = ({ window, trail, range, timeZone, rangeActions, runs = [], activeRun = null }) => {
  const { formatMessage } = useIntl();
  const trackRef = useRef(null);
  const segments = useMemo(() => buildSegments(trail.events, window), [trail.events, window]);
  const markers = useMemo(() => getCameraMarkers(trail.events, window), [trail.events, window]);
  const axisLabels = buildAxisLabels(window);
  const hasSeveralRuns = runs.length > 1;

  // Clicking the bar (not a handle or a marker) moves the clip to the LightMetrics trip under the pointer.
  const handleTrackPointerDown = (event) => {
    if (!hasSeveralRuns) return;
    if (event.target.closest('.request-video-handle, .request-video-marker')) return;
    const rect = trackRef.current.getBoundingClientRect();
    const ratio = Math.min(Math.max((event.clientX - rect.left) / rect.width, 0), 1);
    rangeActions.jumpTo(window.start + ratio * (window.end - window.start));
  };

  return (
    <div className="request-video-timeline">
      <TrailNotice trail={trail} hasSegments={segments.length > 0} />
      <div className="request-video-track" ref={trackRef} onPointerDown={handleTrackPointerDown}>
        {segments.map((segment) => (
          <span
            key={segment.startMs}
            className={`request-video-segment request-video-segment-${segment.kind}`}
            style={{
              left: `${toPercent(segment.startMs, window)}%`,
              width: `${toPercent(segment.endMs, window) - toPercent(segment.startMs, window)}%`,
            }}
          />
        ))}
        {hasSeveralRuns &&
          runs
            .filter((run) => run !== activeRun)
            .map((run) => (
              <span
                key={run.start}
                className="request-video-unavailable"
                title={formatMessage({ id: 'requestVideo_lmTrips_other' })}
                style={{
                  left: `${toPercent(run.start, window)}%`,
                  width: `${toPercent(run.end, window) - toPercent(run.start, window)}%`,
                }}
              />
            ))}
        <span
          className="request-video-selection"
          style={{
            left: `${toPercent(range.start, window)}%`,
            width: `${toPercent(range.end, window) - toPercent(range.start, window)}%`,
          }}
        />
        {markers.map((marker) => (
          <button
            key={marker.id}
            type="button"
            className="request-video-marker"
            style={{ left: `${toPercent(marker.timeMs, window)}%` }}
            aria-label={formatMessage(
              { id: 'requestVideo_marker_label' },
              { time: formatClock(marker.timeMs, timeZone, true) }
            )}
            onClick={() => rangeActions.centerOn(marker.timeMs)}
          />
        ))}
        <TimelineHandle
          edge="start"
          ms={range.start}
          window={window}
          timeZone={timeZone}
          trackRef={trackRef}
          onNudge={rangeActions.nudge}
          onSetTime={rangeActions.setEdge}
        />
        <TimelineHandle
          edge="end"
          ms={range.end}
          window={window}
          timeZone={timeZone}
          trackRef={trackRef}
          onNudge={rangeActions.nudge}
          onSetTime={rangeActions.setEdge}
        />
      </div>
      <div className="request-video-ticks">
        {axisLabels.map((label) => {
          const isLimit = label.kind !== 'tick';
          const classes = [LABEL_ALIGN_CLASS[label.kind]];
          if (isLimit) classes.push('limit');
          return (
            <span key={label.key} className={classes.join(' ').trim()} style={{ left: `${label.percent}%` }}>
              {formatClock(label.ms, timeZone)}
            </span>
          );
        })}
      </div>
      <Legend />
      {hasSeveralRuns && <p className="request-video-hint">{formatMessage({ id: 'requestVideo_lmTrips_hint' })}</p>}
    </div>
  );
};
